import * as React from 'react';

import './style.scss';
import {Button, ButtonColor, ButtonSize, ButtonType} from "@atoms/LinksAndButtons/Button";
import {FurnishingModel} from "features/apartmentViewer/ApartmentViewer/models/FurnishingModel";
import {Spinner} from "@atoms/Loaders/Loader/_spinner";
import {Icon} from "@atoms/Media/Icon";
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {FormattedMessage} from 'react-intl';
import {SiteModel} from "features/apartmentViewer/ApartmentViewer/models/SiteModel";

interface Props {
    currentFurnishing: string | null;
    loading: boolean;
    siteData: SiteModel;
    onClick: (furnishing: FurnishingModel, callback: () => void) => void;
}

export const ApartmentFurnishSelector : React.FC<Props> = ({siteData, onClick, loading, currentFurnishing}) => {

    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [selectedFurnishing, setSelectedFurnishing] = React.useState<string | null>(null);

    const wrapperRef = React.useRef<HTMLDivElement>(null);

    const toggleSelector = () => {
        setIsOpen(!isOpen);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    const translateFurnishing = (furnishing: string) => {
        switch (furnishing) {
            case 'Furnished':
                return getFormattedMessage('jm.features.appartmentViewer.interiorStyle.furnished');
            case 'Empty':
            case 'Omöblerad':
                return getFormattedMessage('jm.features.appartmentViewer.interiorStyle.empty');
            case 'Interior style 1':
            case 'White concept':
                return getFormattedMessage('jm.features.appartmentViewer.interiorStyle.interiorStyle1');
            case 'Interior style 2':
            case 'Dark concept':
                return getFormattedMessage('jm.features.appartmentViewer.interiorStyle.interiorStyle2');
        }

        return furnishing;
    };

    const internalOnClick = (furnishing: FurnishingModel) => {
        setSelectedFurnishing(furnishing.id);
        onClick(furnishing, onNewFurnishingLoaded);
    };

    const onNewFurnishingLoaded = () => {
        setSelectedFurnishing(null);
    };

    React.useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    if(!siteData) return null;

    const getValidFurnishing = () => siteData.furnishings ? siteData.furnishings.filter(furnishing => siteData.activeViews?.includes(furnishing.id)) : [];

    return (
        <div className="apartment-furnishing-selector" ref={wrapperRef}>

            <Button
                onClick={toggleSelector}
                icon={<Icon name="chevron" up={isOpen} />}
                buttonColor={ButtonColor.DEFAULT}
                buttonSize={ButtonSize.SMALL}
                buttonType={ButtonType.GHOST}
            >
                <FormattedMessage id="jm.features.appartmentViewer.chooseInterior" />
            </Button>

            {isOpen && (
                <div className="apartment-furnishing-selector__selector">
                    <ul>
                        {getValidFurnishing().map((furnishing, index) => (
                            <li key={`furnishing-${index}`} onClick={() => internalOnClick(furnishing)} className={currentFurnishing === furnishing.id ? 'active' : ''}>
                                { (selectedFurnishing === furnishing.id && loading) && <Spinner small />}
                                {translateFurnishing(furnishing.title)}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};
